import {
  Box,
  Autocomplete,
  TextField,
  Typography,
  CircularProgress,
  Chip,
  InputAdornment,
} from '@mui/material';
import { School as SchoolIcon, Public as PublicIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { listSchoolsDirect } from '@timetable/api';
import { queryKeys } from '../../lib/queryKeys';
import type { School } from '@timetable/types';

// Special option for "All Schools"
const ALL_SCHOOLS_OPTION: School = {
  id: 'all-schools',
  school_id: '',
  school_name: 'All Schools',
  address: '',
  contact_person: 'Super Admin',
  email: '',
  phone: '',
  logo_url: null,
  primary_color: '#1976D2',
  secondary_color: '#FF9800',
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
};

/**
 * SchoolContextSwitcher Component
 * 
 * Allows Super Admin users to switch between schools to manage different tenants.
 * Features:
 * - Dropdown with all schools
 * - Search/filter functionality
 * - Visual indicator of current school
 * - Only visible for Super Admin users
 * - Updates all data views when context changes
 * 
 * Requirements: 2.7.1, 2.7.2, 2.7.3, 2.7.4, 3.5.1, 3.5.2, 3.5.3, 4.3.1
 */
export function SchoolContextSwitcher() {
  const user = useAuthStore((state) => state.user);
  const schoolContext = useAuthStore((state) => state.schoolContext);
  const setSchoolContext = useAuthStore((state) => state.setSchoolContext);
  const clearSchoolContext = useAuthStore((state) => state.clearSchoolContext);

  // Only show for Super Admin users (Requirement 3.5.1)
  if (user?.role !== 'super_admin') {
    return null;
  }

  // Fetch all schools (Requirement 2.7.2)
  // Requirements: 4.1.1, 4.1.2 - Cache for fast context switching
  const { data: schools, isLoading } = useQuery({
    queryKey: queryKeys.schools.lists(),
    queryFn: listSchoolsDirect,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes - performance optimization
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  // Add "All Schools" option at the beginning
  const schoolOptions = schools ? [ALL_SCHOOLS_OPTION, ...schools] : [ALL_SCHOOLS_OPTION];

  // Get current school object - if schoolContext is null/empty, use ALL_SCHOOLS_OPTION
  const currentSchool = schoolContext 
    ? schools?.find((s) => s.school_id === schoolContext) || ALL_SCHOOLS_OPTION
    : ALL_SCHOOLS_OPTION;

  // Handle school selection (Requirement 2.7.3)
  const handleSchoolChange = (_event: any, newValue: School | null) => {
    if (!newValue || newValue.school_id === '') {
      // "All Schools" selected or cleared
      clearSchoolContext();
    } else {
      setSchoolContext(newValue.school_id);
    }
  };

  return (
    <Box sx={{ minWidth: 240, maxWidth: 260 }}>
      <Autocomplete
        value={currentSchool}
        onChange={handleSchoolChange}
        options={schoolOptions}
        getOptionLabel={(option) => option.school_name}
        loading={isLoading}
        size="small"
        disableClearable
        renderInput={(params) => (
          <TextField
            {...params}
            label={currentSchool?.school_id === '' ? '' : 'School'}
            placeholder="Select school"
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 1)',
                },
                '&.Mui-focused': {
                  backgroundColor: 'rgba(255, 255, 255, 1)',
                },
              },
              '& .MuiInputLabel-root': {
                display: 'none', // Hide label to prevent overlap
              },
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <InputAdornment position="start">
                    {currentSchool?.school_id === '' ? (
                      <PublicIcon fontSize="small" sx={{ color: 'primary.main' }} />
                    ) : (
                      <SchoolIcon fontSize="small" sx={{ color: 'primary.main' }} />
                    )}
                  </InputAdornment>
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.school_id || 'all-schools'}>
            <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {option.school_id === '' ? (
                <PublicIcon fontSize="small" sx={{ color: 'primary.main' }} />
              ) : (
                <SchoolIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              )}
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography 
                    variant="body2" 
                    fontWeight={option.school_id === '' ? 'bold' : 'medium'} 
                    noWrap
                    sx={{ color: option.school_id === '' ? 'primary.main' : 'text.primary' }}
                  >
                    {option.school_name}
                  </Typography>
                  {option.school_id !== '' && (
                    <Chip
                      label={option.school_id}
                      size="small"
                      variant="outlined"
                      sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
                {option.school_id !== '' && (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {option.contact_person} • {option.email}
                  </Typography>
                )}
                {option.school_id === '' && (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    View aggregated data across all schools
                  </Typography>
                )}
              </Box>
            </Box>
          </li>
        )}
        isOptionEqualToValue={(option, value) => option.school_id === value.school_id}
        noOptionsText="No schools registered yet"
        openText="Select school"
        closeText="Close"
      />
    </Box>
  );
}
